import type { InfoClient } from "@nktkas/hyperliquid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "#/hooks/query-keys";
import { isRateLimitError } from "../lib/hyperliquid-error";
import { formatRelativePriceDelta, toNumber } from "#/lib/formatters";
import {
	createTradeRouteSymbol,
	getInfoClient,
	getTradeMarketName,
	getXyzInstrument,
	registerApiCoin,
	stripDexPrefix,
	XYZ_DEX_NAME,
} from "../lib/hyperliquid";
import { useTradeSymbol } from "./use-trade-symbol";

type MetaAndAssetCtxsResponse = Awaited<
	ReturnType<InfoClient["metaAndAssetCtxs"]>
>;

export type TradePerpMarketSummary = {
	routeSymbol: string;
	coin: string;
	displaySymbol: string;
	displayName: string;
	szDecimals: number;
	maxLeverage: number;
	markPrice: number;
	previousDayPrice: number;
	priceChangePercent: number;
	predictedFundingRate: number;
	dayNotionalVolume: number;
	openInterestValue: number;
	dex?: string;
};

function createEmptyMarketData(market: ReturnType<typeof useTradeSymbol>) {
	return {
		...market,
		szDecimals: 5,
		maxLeverage: 0,
		marginMode: undefined,
		markPx: "0",
		midPx: "0",
		prevDayPx: "0",
		funding: "0",
		openInterest: "0",
		dayNtlVlm: "0",
		dayBaseVlm: "0",
		oraclePx: "0",
		impactPxs: ["0", "0"],
		markPrice: 0,
		oraclePrice: 0,
		indexPrice: 0,
		previousDayPrice: 0,
		priceChangePercent: 0,
		predictedFundingRate: 0,
		dayNotionalVolume: 0,
		dayBaseVolume: 0,
		openInterestValue: 0,
	};
}

export type CombinedMetaResponse = {
	main: MetaAndAssetCtxsResponse;
	xyz: MetaAndAssetCtxsResponse | null;
};

export type { MetaAndAssetCtxsResponse };

let xyzMetaFallback: MetaAndAssetCtxsResponse | null = null;

export function getXyzMetaFallback(): MetaAndAssetCtxsResponse | null {
	return xyzMetaFallback;
}

export function setXyzMetaFallback(data: MetaAndAssetCtxsResponse) {
	xyzMetaFallback = data;
}

export function useTradeMetaQuery() {
	const infoClient = getInfoClient();
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: queryKeys.trade.metaAndAssetContexts(),
		queryFn: async (): Promise<CombinedMetaResponse> => {
			let xyzFailed = false;
			const [main, xyz] = await Promise.all([
				infoClient.metaAndAssetCtxs() as Promise<MetaAndAssetCtxsResponse>,
				(
					infoClient.metaAndAssetCtxs({
						dex: XYZ_DEX_NAME,
					}) as Promise<MetaAndAssetCtxsResponse>
				).catch((err) => {
					if (isRateLimitError(err)) {
						xyzFailed = true;
						return null;
					}
					return new Promise<null>((resolve) => setTimeout(resolve, 4_000))
						.then(
							() =>
								infoClient.metaAndAssetCtxs({
									dex: XYZ_DEX_NAME,
								}) as Promise<MetaAndAssetCtxsResponse>,
						)
						.catch(() => {
							xyzFailed = true;
							return null;
						});
				}),
			]);
			if (xyz !== null) {
				xyzMetaFallback = xyz;
			}
			if (xyzFailed && xyz === null) {
				const prev = queryClient.getQueryData<CombinedMetaResponse>(
					queryKeys.trade.metaAndAssetContexts(),
				);
				const cachedXyz = prev?.xyz ?? xyzMetaFallback;
				if (cachedXyz) {
					return { main, xyz: cachedXyz };
				}
			}
			return { main, xyz };
		},
		staleTime: 300_000,
		refetchInterval: (query) => {
			const data = query.state.data as CombinedMetaResponse | undefined;
			if (data && !data.xyz) return 10_000;
			return 60_000;
		},
	});
}

function parseDexMarkets(
	response: MetaAndAssetCtxsResponse,
	dex?: string,
): TradePerpMarketSummary[] {
	const [meta, contexts] = response;
	return meta.universe
		.map((universe, index) => {
			const context = contexts[index];
			if (!context) return null;

			const rawName = universe.name;
			const coin = dex ? stripDexPrefix(rawName) : rawName;
			if (dex && rawName !== coin) registerApiCoin(coin, rawName);
			const markPrice = toNumber(context.markPx);
			const previousDayPrice = toNumber(context.prevDayPx);

			const summary: TradePerpMarketSummary = {
				routeSymbol: createTradeRouteSymbol(coin, dex),
				coin,
				displaySymbol:
					dex === XYZ_DEX_NAME
						? `${getXyzInstrument(coin)}-PERP`
						: `${coin}-PERP`,
				displayName: `${getTradeMarketName(coin, dex)} Perpetual Futures`,
				szDecimals: universe.szDecimals,
				maxLeverage: universe.maxLeverage,
				markPrice,
				previousDayPrice,
				priceChangePercent: formatRelativePriceDelta(
					markPrice,
					previousDayPrice,
				),
				predictedFundingRate: toNumber(context.funding),
				dayNotionalVolume: toNumber(context.dayNtlVlm),
				openInterestValue: toNumber(context.openInterest) * markPrice,
			};
			if (dex) summary.dex = dex;
			return summary;
		})
		.filter(
			(market): market is TradePerpMarketSummary =>
				market !== null && market.dayNotionalVolume > 0,
		);
}

export function useTradePerpMarkets() {
	const metaQuery = useTradeMetaQuery();

	const combined = metaQuery.data;
	let markets: TradePerpMarketSummary[] = [];

	if (combined) {
		const mainMarkets = parseDexMarkets(combined.main);
		const xyzMarkets = combined.xyz
			? parseDexMarkets(combined.xyz, XYZ_DEX_NAME)
			: [];
		markets = [...mainMarkets, ...xyzMarkets].sort(
			(left, right) => right.dayNotionalVolume - left.dayNotionalVolume,
		);
	}

	return {
		...metaQuery,
		markets,
	};
}

function findCoinInMeta(
	response: MetaAndAssetCtxsResponse,
	coin: string,
	isDex = false,
) {
	const [meta, contexts] = response;
	const index = meta.universe.findIndex((item) =>
		isDex ? stripDexPrefix(item.name) === coin : item.name === coin,
	);
	if (index === -1) return null;
	return { universe: meta.universe[index], context: contexts[index] };
}

export function useTradeMarket() {
	const market = useTradeSymbol();
	const metaQuery = useTradeMetaQuery();
	const fallbackMarketData = createEmptyMarketData(market);

	const combined = metaQuery.data;
	const marketData = (() => {
		if (!combined) return fallbackMarketData;

		const found =
			findCoinInMeta(combined.main, market.coin) ??
			(combined.xyz ? findCoinInMeta(combined.xyz, market.coin, true) : null);
		if (!found) return fallbackMarketData;

		const { universe, context } = found;
		const markPrice = toNumber(context.markPx);
		const oraclePrice = toNumber(context.oraclePx);
		const indexPrice = toNumber(context.midPx ?? context.oraclePx);
		const previousDayPrice = toNumber(context.prevDayPx);

		return {
			...market,
			...universe,
			...context,
			markPrice,
			oraclePrice,
			indexPrice,
			previousDayPrice,
			priceChangePercent: formatRelativePriceDelta(markPrice, previousDayPrice),
			predictedFundingRate: toNumber(context.funding),
			dayNotionalVolume: toNumber(context.dayNtlVlm),
			dayBaseVolume: toNumber(context.dayBaseVlm),
			openInterestValue: toNumber(context.openInterest) * markPrice,
		};
	})();

	return {
		...metaQuery,
		market,
		marketData,
	};
}
